import { useParams } from '@tanstack/react-router'
import { message } from 'antd'
import { useRolodexEdit } from 'features/fde'
import { useRolodexEntries } from 'features/rolodex'
import {
  isRevisionMetadata,
  RevisionMetadata,
  RevisionOption,
} from 'pages/reporting/shared/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { GeckoReportingMetadata, RolodexEntryType, SectionId } from 'types'

interface UseMetadataEntryResult {
  metadataEntryId: string
  metadataEntries: readonly GeckoReportingMetadata[]
  isLoading: boolean
  createInitialEntry: (props: {
    unitId: string
    reportTemplateId: string
    inspectionIds: string[]
    visualInspectionIds: string[]
    prePopulatedData?: Record<SectionId, Record<string, unknown>>
  }) => Promise<void>
  publishRevision: () => Promise<string>
  currentRevisionNumber: number
  revisions: RevisionOption[]
  latestRevision: RevisionOption | undefined
  createNewRevision: () => Promise<void>
  isCurrentRevisionPublished: boolean
  refreshMetadataEntry: () => Promise<void>
}

/**
 * Generates a metadata entry ID for a given inspection and revision
 */
const generateMetadataEntryId = (reportId: string, revisionNumber: number) =>
  `${reportId}-metadata-${revisionNumber}`

/**
 * Hook to manage metadata entries and revisions for FDE reports
 *
 * Handles:
 * - Tracking current revision number
 * - Managing metadata entry IDs
 * - Creating new revisions
 * - Publishing revisions
 * - Loading and processing existing revisions
 *
 * @param reportId - The identifier for the report being generated
 * @returns {UseMetadataEntryResult} Metadata management utilities and state
 */
export const useMetadataEntry = (
  reportId: string | undefined,
  requestedMetadataEntryId?: string
): UseMetadataEntryResult => {
  const { siteID } = useParams({ strict: false }) as {
    siteID?: string
  }

  const { getSite, getOrganization } = useRolodexEntries(
    siteID
      ? {
          ids: [siteID],
          type: [RolodexEntryType.SITE],
          links: [RolodexEntryType.ORGANIZATION],
        }
      : undefined
  )

  const { site, organization } = useMemo(() => {
    if (!siteID) {
      return { site: undefined, organization: undefined }
    }
    const site = getSite(siteID)
    const organization = getOrganization(site?.tags.organization)
    return { site, organization }
  }, [siteID, getSite, getOrganization])

  const [currentRevisionNumber, setCurrentRevisionNumber] = useState(1)
  const [metadataEntryId, setMetadataEntryId] = useState(() =>
    reportId ? generateMetadataEntryId(reportId, 1) : ''
  )
  const [isPublishing, setIsPublishing] = useState(false)

  const { rolodexPut, rolodexPatch } = useRolodexEdit()

  const metadataQuery = useMemo(() => {
    return reportId && site
      ? {
          type: RolodexEntryType.REPORTING_METADATA,
          filters: [
            {
              field: 'data',
              key: 'report_id',
              op: '=',
              value: reportId,
            },
          ],
          tags: {
            site: site.id,
          },
        }
      : undefined
  }, [reportId, site])

  const {
    reportingMetadata,
    isLoading,
    forceRefresh: refreshMetadataEntry,
  } = useRolodexEntries(metadataQuery)

  // Process and sort revisions
  const revisions = useMemo(() => {
    if (!reportId) return []

    return reportingMetadata
      .map((entry): RevisionOption => {
        // Need to type check the data structure
        const entryData = entry.data
        let revisionMetadata: RevisionMetadata

        // Handle cases where revisionMetadata might not exist or have the correct shape
        if (isRevisionMetadata(entryData.revisionMetadata)) {
          revisionMetadata = entryData.revisionMetadata
        } else {
          // Default metadata if none exists
          revisionMetadata = {
            revisionNumber: 1,
            createdAt: entry.updated_at,
            isPublished: false,
          }
        }

        return {
          id: entry.id,
          revisionNumber: revisionMetadata.revisionNumber,
          createdAt: revisionMetadata.createdAt,
          publishedAt: revisionMetadata.publishedAt,
          isPublished: revisionMetadata.isPublished,
        }
      })
      .sort((a, b) => b.revisionNumber - a.revisionNumber)
  }, [reportingMetadata, reportId])

  // Get the latest revision
  const latestRevision = useMemo(() => revisions.at(0), [revisions])

  // Update current revision number and metadata entry ID based on revisions
  useEffect(() => {
    if (!reportId || isLoading || isPublishing) return
    if (revisions.length === 0) {
      // No revisions exist - start with revision 1
      setCurrentRevisionNumber(1)
      setMetadataEntryId(generateMetadataEntryId(reportId, 1))
    } else {
      if (requestedMetadataEntryId) {
        const requestedRevision = revisions.find(
          (rev) => rev.id === requestedMetadataEntryId
        )
        if (requestedRevision) {
          setCurrentRevisionNumber(requestedRevision.revisionNumber)
          setMetadataEntryId(requestedRevision.id)
          return
        }
      }

      const unpublishedRevision = revisions.find((rev) => !rev.isPublished)
      if (unpublishedRevision) {
        // Use existing unpublished revision
        setCurrentRevisionNumber(unpublishedRevision.revisionNumber)
        setMetadataEntryId(unpublishedRevision.id)
      } else {
        // No unpublished revisions - use the latest published revision
        const latestPublishedRevision = revisions[0]
        setCurrentRevisionNumber(latestPublishedRevision.revisionNumber)
        setMetadataEntryId(latestPublishedRevision.id)
      }
    }
  }, [isLoading, revisions, reportId, isPublishing, requestedMetadataEntryId])

  /**
   * Creates initial metadata entry with optional pre-populated data
   */
  const createInitialEntry = useCallback(
    async ({
      unitId,
      reportTemplateId,
      inspectionIds,
      visualInspectionIds,
      prePopulatedData,
    }: {
      unitId: string
      reportTemplateId: string
      inspectionIds: string[]
      visualInspectionIds: string[]
      prePopulatedData?: Record<SectionId, Record<string, unknown>>
    }) => {
      if (!reportId) {
        console.warn('Cannot create initial entry: No report ID provided')
        return
      }

      if (!site || !organization) {
        throw new Error(
          'Cannot create initial entry: Missing site or organization'
        )
      }

      try {
        const revisionMetadata: RevisionMetadata = {
          revisionNumber: currentRevisionNumber,
          createdAt: new Date().toISOString(),
          isPublished: false,
        }

        const emptyEntry = {
          id: metadataEntryId,
          type: RolodexEntryType.REPORTING_METADATA,
          data: {
            report_template_id: reportTemplateId,
            inspections: inspectionIds,
            visual_inspections: visualInspectionIds,
            section_info: prePopulatedData || {},
            revision_metadata: revisionMetadata,
            report_id: reportId,
          },
          tags: {
            site: site.id,
            organization: organization.id,
            unit: unitId,
          },
        }

        await rolodexPut([emptyEntry])
        await refreshMetadataEntry()
      } catch {
        message.error('Failed to create initial metadata entry')
      }
    },
    [
      metadataEntryId,
      currentRevisionNumber,
      rolodexPut,
      refreshMetadataEntry,
      site,
      organization,
      reportId,
    ]
  )

  /**
   * Creates a new empty revision after a published one
   */
  const createNewRevision = useCallback(async () => {
    if (!site || !organization || !metadataEntryId || !reportId) {
      throw new Error('Cannot create new revision: Missing required IDs')
    }

    try {
      setIsPublishing(true) // Reuse publishing state to prevent concurrent operations

      // Get the current entry to copy basic structure
      const currentEntry = reportingMetadata.find(
        (entry) => entry.id === metadataEntryId
      )

      if (!currentEntry) {
        throw new Error('Current metadata entry not found')
      }

      // Create new revision number
      const newRevisionNumber = currentRevisionNumber + 1
      const newMetadataEntryId = generateMetadataEntryId(
        reportId,
        newRevisionNumber
      )

      // Create empty entry with basic structure
      const newEntry = {
        id: newMetadataEntryId,
        type: RolodexEntryType.FDE_INSPECTION_METADATA,
        data: {
          report_template_id: currentEntry.data.reportTemplateId,
          section_info: {},
          revision_metadata: {
            revisionNumber: newRevisionNumber,
            createdAt: new Date().toISOString(),
            isPublished: false,
          },
          report_id: reportId,
        },
        tags: {
          site: site.id,
          organization: organization.id,
        },
      }

      await rolodexPut([newEntry])

      // Update local state
      setCurrentRevisionNumber(newRevisionNumber)
      setMetadataEntryId(newMetadataEntryId)

      message.success(`Successfully created new revision ${newRevisionNumber}`)
    } catch (error) {
      console.error('Failed to create new revision:', error)
      message.error('Failed to create new revision')
    } finally {
      setIsPublishing(false)
    }
  }, [
    metadataEntryId,
    currentRevisionNumber,
    reportingMetadata,
    rolodexPut,
    site,
    organization,
    reportId,
  ])

  /**
   * Publishes current revision and creates a new draft
   */
  const publishRevision = useCallback(async () => {
    if (!site || !organization || !metadataEntryId) {
      throw new Error('Cannot publish revision: Missing required IDs')
    }

    try {
      setIsPublishing(true)

      // Mark current revision as published
      await rolodexPatch([
        {
          id: metadataEntryId,
          data: {
            revision_metadata: {
              revisionNumber: currentRevisionNumber,
              createdAt: revisions.find((r) => r.id === metadataEntryId)
                ?.createdAt,
              publishedAt: new Date().toISOString(),
              isPublished: true,
            },
            lock_state: {
              isLocked: true,
              startedSignoff: true,
              lastLockTimestamp: new Date().toISOString(),
            },
          },
        },
      ])

      return metadataEntryId
    } finally {
      setIsPublishing(false)
    }
  }, [
    metadataEntryId,
    currentRevisionNumber,
    revisions,
    rolodexPatch,
    site,
    organization,
  ])

  return {
    metadataEntryId,
    metadataEntries: reportingMetadata,
    isLoading,
    createInitialEntry,
    publishRevision,
    currentRevisionNumber,
    revisions,
    latestRevision,
    createNewRevision,
    isCurrentRevisionPublished: latestRevision?.isPublished ?? false,
    refreshMetadataEntry,
  }
}
